import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useMemo } from 'react'
import { z } from 'zod'
import { DrilldownButton } from '~/components/button'
import { DateRangePicker } from '~/components/date-picker'
import Spacer from '~/components/spacer'
import {
  ChannelsCommandList,
  CommandPopover,
  GroupsCommandList,
  TagsCommandList,
  UsersCommandList,
} from '~/features/command-lists'
import { ConversationDrivers } from '~/features/conversation-drivers'
import { UploadPanel, useUploadContext } from '~/features/importer'
import { useFilterAccess } from '~/features/filter-control'
import { MetricCell } from '~/features/metric-cell'
import { MyScorecard, MyScorecardFilters } from '~/features/my-scorecard'
import { MissingScorecardsNotice } from '~/features/setup/notices/missing-scorecards-notice'
import {
  useCurrentOrganization,
  useCurrentUserSetting,
  useTimespanPreference,
  useTitle,
  useUserHomepageView,
} from '~/hooks'
import { ServiceQualityOverview } from '~/modules/analytics/service-quality'

import { FilterCollection } from '@hap/analytics'
import { Button } from '@hap/components'
import { HOMEPAGE_VIEW_SCORECARD } from '@hap/constants'
import { Timespan } from '@hap/utils'

import { DurationFilterPopover } from './-components/duration-filter-popover'
import {
  type DashboardFilters,
  convertToAnalyticsFilters,
  dashboardFilters,
} from './-components/filter-utils'
import { TopDrivers } from './-components/top-drivers'
import { WelcomeBanner } from './-components/welcome-banner'
import { WelcomeHeader } from './-components/welcome-header'

const searchParamsSchema = z.object({
  filters: dashboardFilters.default({}),
  timespan: Timespan.Info.optional(),
})

export const Route = createFileRoute('/_authenticated/$alias/_app/')({
  component: DashboardRoute,
  validateSearch: searchParamsSchema,
})

function DashboardRoute() {
  const { isFilterAllowed } = useFilterAccess()
  const { filters, timespan: selectedTimespan } = Route.useSearch()
  const { timespan, setTimespanPreference } = useTimespanPreference({ selectedTimespan })
  const navigate = useNavigate({ from: Route.fullPath })
  const organization = useCurrentOrganization()
  const [currentView] = useUserHomepageView()
  const { isPanelVisible } = useUploadContext()
  // User preference persistence for channels
  const [userChannels, setUserChannels] = useCurrentUserSetting(
    'dashboard',
    'channels',
    [] as string[],
  )

  // Initialize channel filter from user setting if no URL filter
  useEffect(() => {
    if (!filters.channelIds && userChannels.length > 0) {
      navigate({
        search: (prev) => ({
          ...prev,
          filters: { ...prev.filters, channelIds: { op: 'in', values: userChannels } },
        }),
        replace: true,
      })
    }
  }, [])

  const hasResettableFilters =
    (isFilterAllowed('channelId') && !!filters.channelIds) ||
    (isFilterAllowed('tagIds') && !!filters.tagIds) ||
    (isFilterAllowed('groupIds') && !!filters.groupIds) ||
    (isFilterAllowed('assignedSupervisorId') && !!filters.reportsToIds) ||
    (isFilterAllowed('intentId') && !!filters.intentIds) ||
    (isFilterAllowed('handleTime') && !!filters.handleTime)
  const daterange = Timespan.toStaticTimespan(timespan)

  // Convert filters to analytics query format for MetricCell, ConversationDrivers, etc.
  const analyticsQueryContext = useMemo(() => {
    const analyticsFilters = convertToAnalyticsFilters(filters)

    return {
      timespan: {
        start: daterange.start.toISOString(),
        end: daterange.end.toISOString(),
      },
      filters: analyticsFilters,
    }
  }, [filters, daterange])

  useTitle('Dashboard')

  // Scorecard filters state (converted from dashboard filters for scorecard view)
  const scorecardFilters: MyScorecardFilters = useMemo(() => {
    return {
      channelIds: filters.channelIds,
    }
  }, [filters.channelIds])

  function handleScorecardFiltersChange(newFilters: MyScorecardFilters) {
    navigate({
      search: (prev) => ({
        ...prev,
        filters: {
          ...prev.filters,
          channelIds: newFilters.channelIds,
        },
      }),
    })
  }

  function handleScorecardTimespanChange(newTimespan: Timespan.Info) {
    setTimespanPreference(newTimespan)
    navigate({
      search: (prev) => ({
        ...prev,
        timespan: newTimespan,
      }),
    })
  }

  // Render scorecard view
  if (currentView === HOMEPAGE_VIEW_SCORECARD) {
    return (
      <>
        {organization.setup?.welcome === false && <WelcomeBanner />}
        <MissingScorecardsNotice />

        <div className="layout pt-6">
          <WelcomeHeader />
        </div>

        <MyScorecard
          filters={scorecardFilters}
          timespan={timespan}
          onFiltersChange={handleScorecardFiltersChange}
          onTimespanChange={handleScorecardTimespanChange}
        />
      </>
    )
  }

  // Render analytics view (default)
  return (
    <>
      {organization.setup?.welcome === false && <WelcomeBanner />}

      <div className="px-6 pt-6">
        <WelcomeHeader />
      </div>

      {isPanelVisible && <UploadPanel variant="full" />}

      <MissingScorecardsNotice />

      <div className="flex flex-wrap items-center gap-2 p-6">
        {hasResettableFilters && (
          <Button variant="ghost" onClick={resetFilters} icon="arrow-counter-clockwise">
            Reset
          </Button>
        )}

        <DateRangePicker
          value={timespan}
          onValueChange={(value) => {
            setTimespanPreference(value)
            navigate({ search: (prev) => ({ ...prev, timespan: value }) })
          }}
        />

        {(isFilterAllowed('channelId') || !!filters.channelIds) && (
          <CommandPopover
            label="Channels"
            collection={FilterCollection.Channels}
            selected={filters.channelIds?.values || []}
            disabled={!isFilterAllowed('channelId')}
          >
            <ChannelsCommandList
              multiselect
              onSelect={(ids) => handleFilterChange('channelIds', ids)}
            />
          </CommandPopover>
        )}

        {(isFilterAllowed('tagIds') || !!filters.tagIds) && (
          <CommandPopover
            label="Highlights"
            collection={FilterCollection.Tags}
            selected={filters.tagIds?.values || []}
            disabled={!isFilterAllowed('tagIds')}
          >
            <TagsCommandList
              multiselect
              onSelect={(ids) => handleFilterChange('tagIds', ids)}
            />
          </CommandPopover>
        )}

        {(isFilterAllowed('groupIds') || !!filters.groupIds) && (
          <CommandPopover
            label="Groups"
            collection={FilterCollection.Groups}
            selected={filters.groupIds?.values || []}
            disabled={!isFilterAllowed('groupIds')}
          >
            <GroupsCommandList
              multiselect
              onSelect={(ids) => handleFilterChange('groupIds', ids)}
            />
          </CommandPopover>
        )}

        {(isFilterAllowed('assignedSupervisorId') || !!filters.reportsToIds) && (
          <CommandPopover
            label="Reports To"
            collection={FilterCollection.Users}
            selected={filters.reportsToIds?.values || []}
            disabled={!isFilterAllowed('assignedSupervisorId')}
          >
            <UsersCommandList
              multiselect
              onSelect={(ids) => handleFilterChange('reportsToIds', ids)}
              contextFilters={{
                subordinates: {
                  $exists: true,
                },
              }}
            />
          </CommandPopover>
        )}

        {(isFilterAllowed('handleTime') || !!filters.handleTime) && (
          <DurationFilterPopover
            value={filters.handleTime}
            disabled={!isFilterAllowed('handleTime')}
            onChange={(value) =>
              navigate({
                search: (prev) => ({
                  ...prev,
                  filters: { ...prev.filters, handleTime: value },
                }),
              })
            }
          />
        )}
      </div>

      <div className="border-b pb-10">
        <div className="mb-4 grid grid-cols-4 gap-4 px-6">
          <MetricCell
            queryContext={analyticsQueryContext}
            field="id"
            label="Interactions Analyzed"
            drilldown
          />
          <MetricCell
            queryContext={analyticsQueryContext}
            field="handledBy"
            label="Active Agents"
          />

          <MetricCell
            queryContext={analyticsQueryContext}
            field="averageEvaluationScore"
            precision={2}
            operation="avg"
            format="percent"
            label="Average Evaluation Score"
          />
        </div>
        <ConversationDrivers queryContext={analyticsQueryContext} />
        {organization.id === '69bcf51e5069a645e60d0d85' && (
          <TopDrivers queryContext={analyticsQueryContext} />
        )}
        <ServiceQualityOverview queryContext={analyticsQueryContext} />
      </div>

      <Spacer y="2" />
      <div className="grid px-6">
        <DrilldownButton size="lg" variant="secondary" className="w-full">
          View Interactions
        </DrilldownButton>
      </div>
    </>
  )

  function handleFilterChange(field: keyof DashboardFilters, values: string[]) {
    navigate({
      search: (prev) => {
        // Persist channel preference
        if (field === 'channelIds') {
          setUserChannels(values)
        }

        return {
          ...prev,
          filters: {
            ...prev.filters,
            [field]: values.length > 0 ? { op: 'in', values } : undefined,
          },
        }
      },
    })
  }

  function resetFilters() {
    if (isFilterAllowed('channelId')) setUserChannels([])
    navigate({
      search: (prev) => ({
        ...prev,
        filters: {
          ...(!isFilterAllowed('channelId') && { channelIds: prev.filters.channelIds }),
          ...(!isFilterAllowed('tagIds') && { tagIds: prev.filters.tagIds }),
          ...(!isFilterAllowed('groupIds') && { groupIds: prev.filters.groupIds }),
          ...(!isFilterAllowed('assignedSupervisorId') && {
            reportsToIds: prev.filters.reportsToIds,
          }),
          ...(!isFilterAllowed('intentId') && { intentIds: prev.filters.intentIds }),
          ...(!isFilterAllowed('handleTime') && { handleTime: prev.filters.handleTime }),
        },
      }),
    })
  }
}
